import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Card } from 'react-bootstrap';
import { useUploadCellphonesMutation } from '../../slices/devicesApiSlice';
import { toast } from 'react-toastify';
import Loader from '../Loader';

const CellphoneUpload = ({ visible = false }) => {
  const [isVisible, setIsVisible] = useState(visible);
  const [file, setFile] = useState(null);
  
  // Update isVisible when visible prop changes
  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);
  
  const [uploadCellphones, { isLoading, error }] = useUploadCellphonesMutation();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setUploadSuccess(false);
    setUploadResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please select a CSV file to upload');
      return;
    }

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error('Please upload a valid CSV file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await uploadCellphones(formData).unwrap();
      setUploadSuccess(true);
      setUploadResult(result);
      toast.success('Cellphone CSV uploaded and processed successfully');
      setFile(null);
      // Reset the file input
      const fileInput = document.getElementById('cellphoneCsvFile');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      toast.error(err?.data?.message || err.error || 'Failed to upload CSV file');
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Card className="my-3 p-3">
      <Card.Header as="h5">Upload Cellphone CSV</Card.Header>
      <Card.Body>
        <Card.Text>
          Upload a CSV file to update cellphone records. This will delete all existing devices with name "Cellphone" and create new ones from the CSV data.
        </Card.Text>
        <Card.Text>
          <strong>CSV Format (Columns A-F):</strong><br/>
          A: User<br/>
          B: Number<br/>
          C: Name (Device Name)<br/>
          D: Type<br/>
          E: Vendor<br/>
          F: Site Abbreviation (for lookup)
        </Card.Text>
        
        {error && <Alert variant="danger">{error?.data?.message || error.error}</Alert>}
        
        {uploadSuccess && uploadResult && (
          <Alert variant="success">
            CSV processed successfully! Created {uploadResult.devicesCreated} new cellphone records.
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="cellphoneCsvFile" className="mb-3">
            <Form.Label>Select CSV File</Form.Label>
            <Form.Control 
              type="file" 
              onChange={handleFileChange}
              accept=".csv"
            />
            <Form.Text className="text-muted">
              Only CSV files are accepted
            </Form.Text>
          </Form.Group>
          
          <Button 
            variant="primary" 
            type="submit" 
            disabled={!file || isLoading}
          >
            {isLoading ? <Loader /> : 'Upload and Process'}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CellphoneUpload;
